// Pure kern van de klassieke I Tjing: worp, trigrammen, hexagrammen en afgeleide figuren.
// Geen Angular, geen Date. Lijnen altijd onder→boven (index 0 = lijn 1); figuur = 6 bits, 1 = yang.
import { ICHING_HEXAGRAM_MATRIX, ICHING_TRIGRAM_TABLE_ORDER, ICHING_TRIGRAMS } from './iching-trigrams.const';
import {
  IChingBalanceLevel, IChingCoinFace, IChingConcentration, IChingFigure, IChingLinePosition, IChingLineValue, IChingReading,
  IChingSancai, IChingTrigramId,
} from './iching.models';

const TRIGRAM_BY_BITS: ReadonlyMap<string, IChingTrigramId> = new Map(
  Object.values(ICHING_TRIGRAMS).map(t => [t.bits, t.id] as const));

/** King Wen-nummer → figuur, eenmalig opgebouwd uit ICHING_HEXAGRAM_MATRIX. */
const FIGURE_BY_HEXAGRAM: ReadonlyMap<number, IChingFigure> = new Map(
  ICHING_HEXAGRAM_MATRIX.flatMap((row, upperIndex) => row.map((n, lowerIndex) => [
    n,
    ICHING_TRIGRAMS[ICHING_TRIGRAM_TABLE_ORDER[lowerIndex]].bits + ICHING_TRIGRAMS[ICHING_TRIGRAM_TABLE_ORDER[upperIndex]].bits,
  ] as const)));

// ---------- worp ----------

/** Cryptografisch willekeurig getal in [0, 1). */
export function cryptoRandom(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32;
}

/** Drie munten naar lijnwaarde: kop = 3, munt = 2, som = 6/7/8/9. */
export function coinThrowToLine(coins: readonly [IChingCoinFace, IChingCoinFace, IChingCoinFace]): IChingLineValue {
  return coins.reduce((sum, face) => sum + (face === 'head' ? 3 : 2), 0) as IChingLineValue;
}

/** Virtuele worp van drie munten; rng() < 0.5 geeft kop. */
export function throwCoins(rng: () => number = cryptoRandom): [IChingCoinFace, IChingCoinFace, IChingCoinFace] {
  const face = (): IChingCoinFace => (rng() < 0.5 ? 'head' : 'tail');
  return [face(), face(), face()];
}

/** Eén lijn via de muntenmethode (6 = 1/8, 7 = 3/8, 8 = 3/8, 9 = 1/8). */
export function coinLine(rng?: () => number): IChingLineValue {
  return coinThrowToLine(throwCoins(rng));
}

/** Eén lijn via de duizendbladverdeling (Zhu Xi): 6 = 1/16, 7 = 5/16, 8 = 7/16, 9 = 3/16. */
export function yarrowLine(rng: () => number = cryptoRandom): IChingLineValue {
  const r = rng() * 16;
  if (r < 1) return 6;
  if (r < 6) return 7;
  if (r < 13) return 8;
  return 9;
}

// ---------- lijnen ----------

/** Hele lijn (7 of 9). */
export function isYang(v: IChingLineValue): boolean {
  return v === 7 || v === 9;
}

/** Bewegende lijn (6 of 9). */
export function isMoving(v: IChingLineValue): boolean {
  return v === 6 || v === 9;
}

/** Omgeslagen lijn: 6 wordt 7, 9 wordt 8, rustende lijnen blijven. */
export function changeLine(v: IChingLineValue): IChingLineValue {
  if (v === 6) return 7;
  if (v === 9) return 8;
  return v;
}

/** Bewegende posities, 1-based en oplopend (de onderste eerst). */
export function movingPositions(lines: readonly IChingLineValue[]): IChingLinePosition[] {
  return lines.flatMap((v, i) => (isMoving(v) ? [(i + 1) as IChingLinePosition] : []));
}

// ---------- trigrammen en hexagrammen ----------

/** Drie bits (onder→boven) naar trigram; gooit bij ongeldige invoer. */
export function trigramOf(bits3: string): IChingTrigramId {
  const id = TRIGRAM_BY_BITS.get(bits3);
  if (!id) throw new Error(`Geen trigram voor '${bits3}'`);
  return id;
}

/** Onderste (lijnen 1-3) en bovenste (lijnen 4-6) trigram van een figuur. */
export function trigramsOf(figure: IChingFigure): { lower: IChingTrigramId; upper: IChingTrigramId } {
  return { lower: trigramOf(figure.slice(0, 3)), upper: trigramOf(figure.slice(3, 6)) };
}

/** King Wen-nummer voor boven- en ondertrigram. */
export function hexagramNumber(upper: IChingTrigramId, lower: IChingTrigramId): number {
  return ICHING_HEXAGRAM_MATRIX[ICHING_TRIGRAM_TABLE_ORDER.indexOf(upper)][ICHING_TRIGRAM_TABLE_ORDER.indexOf(lower)];
}

/** Figuur van lijnwaarden; bewegende lijnen tellen als wat ze nu zijn. */
export function figureOf(lines: readonly IChingLineValue[]): IChingFigure {
  return lines.map(v => (isYang(v) ? '1' : '0')).join('');
}

/** Gegooid hexagram (King Wen) uit lijnwaarden. */
export function linesToHexagram(lines: readonly IChingLineValue[]): number {
  const { upper, lower } = trigramsOf(figureOf(lines));
  return hexagramNumber(upper, lower);
}

/** Figuur van King Wen-nummer n; gooit buiten 1..64. */
export function hexagramBits(n: number): IChingFigure {
  const figure = FIGURE_BY_HEXAGRAM.get(n);
  if (!figure) throw new Error(`Ongeldig hexagramnummer ${n}`);
  return figure;
}

/**
 * Het Unicode-teken van King Wen-nummer n (blok U+4DC0, in King Wen-volgorde: 1 = ䷀, 64 = ䷿),
 * voor tekst zonder figuur zoals de Markdown-export; gooit buiten 1..64.
 */
export function hexagramGlyph(n: number): string {
  if (!Number.isInteger(n) || n < 1 || n > 64) throw new Error(`Ongeldig hexagramnummer ${n}`);
  return String.fromCodePoint(0x4dc0 + n - 1);
}

/** Lijnwaarden uit hexagramnummer plus bewegende posities (handmatige invoer). */
export function linesFromHexagram(n: number, moving: readonly IChingLinePosition[]): IChingLineValue[] {
  return hexagramBits(n).split('').map((bit, i) => {
    const isMovingLine = moving.includes((i + 1) as IChingLinePosition);
    if (bit === '1') return isMovingLine ? 9 : 7;
    return isMovingLine ? 6 : 8;
  });
}

/** Omslaghexagram (alle bewegende lijnen omgezet); undefined zonder bewegende lijnen. */
export function changedHexagram(lines: readonly IChingLineValue[]): number | undefined {
  if (movingPositions(lines).length === 0) return undefined;
  return linesToHexagram(lines.map(changeLine));
}

/** Kerntrigrammen: onder = lijnen 2-3-4, boven = lijnen 3-4-5. */
export function nuclearTrigrams(figure: IChingFigure): { lower: IChingTrigramId; upper: IChingTrigramId } {
  return { lower: trigramOf(figure.slice(1, 4)), upper: trigramOf(figure.slice(2, 5)) };
}

/** Kernhexagram: bovenste kerntrigram boven, onderste kerntrigram onder. */
export function nuclearHexagram(figure: IChingFigure): number {
  const { lower, upper } = nuclearTrigrams(figure);
  return hexagramNumber(upper, lower);
}

/** Omhullende trigrammen (baoti): rond onderste kern lijnen 1-5-6, rond bovenste kern lijnen 1-2-6. */
export function envelopingTrigrams(figure: IChingFigure): { aroundLower: IChingTrigramId; aroundUpper: IChingTrigramId } {
  return {
    aroundLower: trigramOf(figure[0] + figure[4] + figure[5]),
    aroundUpper: trigramOf(figure[0] + figure[1] + figure[5]),
  };
}

/** Resonantietrigram: paren 1-4, 2-5, 3-6; verschillend = yang, gelijk = yin (XOR). */
export function resonanceTrigram(figure: IChingFigure): IChingTrigramId {
  const bits = [0, 1, 2].map(i => (figure[i] === figure[i + 3] ? '0' : '1')).join('');
  return trigramOf(bits);
}

// ---------- evenwicht en bewegende lijnen ----------

/** Aantal bewegende lijnen als niveau: 0 stable, 1 one, 2 two, 3 of meer many. */
export function balanceLevel(lines: readonly IChingLineValue[]): IChingBalanceLevel {
  const count = movingPositions(lines).length;
  if (count === 0) return 'stable';
  if (count === 1) return 'one';
  if (count === 2) return 'two';
  return 'many';
}

/** Zitten de bewegende lijnen alleen onder, alleen boven, of verspreid? */
export function movingConcentration(lines: readonly IChingLineValue[]): IChingConcentration {
  const positions = movingPositions(lines);
  if (positions.length === 0) return 'none';
  if (positions.every(p => p <= 3)) return 'lower';
  if (positions.every(p => p >= 4)) return 'upper';
  return 'spread';
}

/**
 * Samenvattende trigram(men) van de bewegende lijnen, alleen bij 3-5 bewegende lijnen:
 * precies 3 geeft één trigram; 4 of 5 geeft de eerste drie en de laatste drie (overlappend).
 */
export function movingLineTrigrams(lines: readonly IChingLineValue[]): IChingTrigramId[] {
  const positions = movingPositions(lines);
  const trigramAt = (ps: readonly IChingLinePosition[]): IChingTrigramId =>
    trigramOf(ps.map(p => (isYang(lines[p - 1]) ? '1' : '0')).join(''));
  if (positions.length === 3) return [trigramAt(positions)];
  if (positions.length === 4 || positions.length === 5) return [trigramAt(positions.slice(0, 3)), trigramAt(positions.slice(-3))];
  return [];
}

/** Lai Zhide-trigram bij een bewegende lijn: yang Donder/Water/Berg, yin Wind/Vuur/Meer (onder/midden/boven). */
export function laiZhideTrigram(position: IChingLinePosition, value: 6 | 9): IChingTrigramId {
  const slot = (position - 1) % 3;
  const trigrams: readonly IChingTrigramId[] = value === 9 ? ['zhen', 'kan', 'gen'] : ['xun', 'li', 'dui'];
  return trigrams[slot];
}

/** Drie Machten: lijnen 1-2 aarde, 3-4 mens, 5-6 hemel. */
export function sancaiOf(position: IChingLinePosition): IChingSancai {
  if (position <= 2) return 'earth';
  if (position <= 4) return 'human';
  return 'heaven';
}

/** Volledige afleiding van een worp van 6 lijnen; gooit bij een ander aantal. */
export function analyse(lines: readonly IChingLineValue[]): IChingReading {
  if (lines.length !== 6) throw new Error(`Verwacht 6 lijnen, kreeg ${lines.length}`);
  const figure = figureOf(lines);
  const { lower, upper } = trigramsOf(figure);
  const nuclear = nuclearTrigrams(figure);
  const enveloping = envelopingTrigrams(figure);
  const moving = movingPositions(lines);
  const changed = changedHexagram(lines);
  const changedTrigrams = changed === undefined ? undefined : trigramsOf(figureOf(lines.map(changeLine)));
  return {
    lines: [...lines],
    primary: hexagramNumber(upper, lower),
    figure,
    lower,
    upper,
    movingPositions: moving,
    balance: balanceLevel(lines),
    concentration: movingConcentration(lines),
    movingLineTrigrams: movingLineTrigrams(lines),
    laiZhide: moving.map(position => {
      const yang = isYang(lines[position - 1]);
      return { position, trigram: laiZhideTrigram(position, yang ? 9 : 6), yang };
    }),
    resonance: resonanceTrigram(figure),
    nuclearLower: nuclear.lower,
    nuclearUpper: nuclear.upper,
    nuclearHexagram: hexagramNumber(nuclear.upper, nuclear.lower),
    envelopingLower: enveloping.aroundLower,
    envelopingUpper: enveloping.aroundUpper,
    changed,
    changedLower: changedTrigrams?.lower,
    changedUpper: changedTrigrams?.upper,
  };
}
